import { useEffect, useRef, useState } from "react";
import HeroSection from "./components/HeroSection";
import ConceptSection from "./components/ConceptSection";
import ExperienceSection from "./components/ExperienceSection";
import RuleSection from "./components/RuleSection";
import LineupSection from "./components/LineupSection";
import InfoSection from "./components/InfoSection";
import CodeSection from "./components/CodeSection";
import CtaSection from "./components/CtaSection";
import SeoHead from "@/components/feature/SeoHead";

const HOME_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Event",
  name: "MAVERICK vol.5",
  description:
    "DNA of the New Generation — アンダーグラウンドテクノパーティー MAVERICK vol.5。Diskonnected（Taiwan）、OCCA（Japan）、Iron（Japan）が集結。",
  startDate: "2025-01-01",
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  organizer: {
    "@type": "Organization",
    name: "MAVERICK",
    url: import.meta.env.VITE_SITE_URL ?? "https://example.com",
  },
  performer: [
    { "@type": "MusicGroup", name: "Diskonnected" },
    { "@type": "MusicGroup", name: "OCCA" },
    { "@type": "MusicGroup", name: "Iron" },
  ],
  url: import.meta.env.VITE_SITE_URL ?? "https://example.com",
  image: "https://public.readdy.ai/ai/img_res/97ee081b-88e9-4107-a425-874736cbac0a.png",
};

export default function Home() {
  const cursorDotRef = useRef<HTMLDivElement>(null);
  const cursorRingRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Custom cursor
  useEffect(() => {
    const dot = cursorDotRef.current;
    const ring = cursorRingRef.current;
    if (!dot || !ring) return;

    let ringX = 0;
    let ringY = 0;
    let dotX = 0;
    let dotY = 0;
    let raf: number;

    const onMove = (e: MouseEvent) => {
      dotX = e.clientX;
      dotY = e.clientY;
    };

    const animate = () => {
      ringX += (dotX - ringX) * 0.12;
      ringY += (dotY - ringY) * 0.12;
      dot.style.transform = `translate(${dotX - 4}px, ${dotY - 4}px)`;
      ring.style.transform = `translate(${ringX - 16}px, ${ringY - 16}px)`;
      raf = requestAnimationFrame(animate);
    };

    const onEnterInteractive = () => {
      ring.style.width = "40px";
      ring.style.height = "40px";
      ring.style.marginLeft = "-20px";
      ring.style.marginTop = "-20px";
      ring.style.opacity = "0.5";
    };

    const onLeaveInteractive = () => {
      ring.style.width = "32px";
      ring.style.height = "32px";
      ring.style.marginLeft = "0";
      ring.style.marginTop = "0";
      ring.style.opacity = "0.25";
    };

    document.addEventListener("mousemove", onMove);
    raf = requestAnimationFrame(animate);

    const interactives = document.querySelectorAll("a, button, [data-interactive]");
    interactives.forEach((el) => {
      el.addEventListener("mouseenter", onEnterInteractive);
      el.addEventListener("mouseleave", onLeaveInteractive);
    });

    return () => {
      document.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  const navLinks = [
    { label: "CONCEPT", href: "#concept" },
    { label: "EXPERIENCE", href: "#experience" },
    { label: "RULE", href: "#rule" },
    { label: "LINEUP", href: "#lineup" },
    { label: "INFO", href: "#info" },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div
      className="relative"
      style={{
        background: "#000000",
        cursor: "none",
        scrollBehavior: "smooth",
      }}
    >
      <SeoHead
        title="MAVERICK vol.5 | DNA of the New Generation | Underground Techno Party"
        description="MAVERICK vol.5 — DNA of the New Generation。Diskonnected（Taiwan）、OCCA（Japan）、Iron（Japan）が集結するアンダーグラウンドテクノパーティー。2025年開催。"
        keywords="MAVERICK, underground techno, Diskonnected, OCCA, Iron, techno party Japan"
        canonical={`${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/`}
        ogImage="https://public.readdy.ai/ai/img_res/97ee081b-88e9-4107-a425-874736cbac0a.png"
        jsonLd={HOME_JSONLD}
      />
      {/* Custom cursor */}
      <div
        ref={cursorDotRef}
        className="fixed top-0 left-0 z-[9999] pointer-events-none rounded-full bg-white"
        style={{ width: "8px", height: "8px", willChange: "transform" }}
      />
      <div
        ref={cursorRingRef}
        className="fixed top-0 left-0 z-[9998] pointer-events-none rounded-full border border-white/25"
        style={{
          width: "32px",
          height: "32px",
          willChange: "transform",
          transition: "width 0.3s ease, height 0.3s ease, opacity 0.3s ease",
        }}
      />

      {/* Hamburger nav overlay */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="fixed top-8 right-8 md:right-14 z-[200] flex flex-col gap-[5px] p-2 cursor-pointer whitespace-nowrap"
        aria-label="Menu"
      >
        <span
          className="block w-6 h-px bg-white/50"
          style={{
            transition: "transform 0.3s ease, opacity 0.3s ease",
            transform: menuOpen ? "rotate(45deg) translate(4px, 4px)" : "none",
          }}
        />
        <span
          className="block w-4 h-px bg-white/50"
          style={{
            transition: "opacity 0.3s ease",
            opacity: menuOpen ? 0 : 1,
          }}
        />
        <span
          className="block w-6 h-px bg-white/50"
          style={{
            transition: "transform 0.3s ease, opacity 0.3s ease",
            transform: menuOpen ? "rotate(-45deg) translate(4px, -4px)" : "none",
          }}
        />
      </button>

      {/* Nav menu */}
      <div
        className="fixed inset-0 z-[150] flex flex-col items-center justify-center"
        style={{
          background: "rgba(0,0,0,0.97)",
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? "all" : "none",
          transition: "opacity 0.5s ease",
        }}
      >
        <nav className="flex flex-col items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="text-white/50 hover:text-white/90 tracking-[0.3em] text-sm uppercase cursor-pointer whitespace-nowrap"
              style={{
                fontFamily: "'Courier New', monospace",
                transition: "color 0.3s ease",
              }}
            >
              {link.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Page sections */}
      <HeroSection />
      <ConceptSection />
      <ExperienceSection />
      <RuleSection />
      <LineupSection />
      <InfoSection />
      <CodeSection />
      <CtaSection />
    </div>
  );
}
